import React, { useState, useEffect, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Spinner,
  Alert,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import classnames from "classnames";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { Fn_FillListData } from "../../store/Functions";
import axios from "axios";
import toastr from "toastr";

const AddEdit_CustomNotification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Initial passed state if coming from "Duplicate" or "Resend"
  const editData = location.state?.campaign || null;

  // Form State
  const [audienceType, setAudienceType] = useState(editData?.AudienceType || "Customer"); // "Customer", "Driver", "Both"
  const [conditionType, setConditionType] = useState(editData?.ConditionType || "ALL");
  const [conditionDays, setConditionDays] = useState(editData?.ConditionDays || 7);
  const [conditionValue, setConditionValue] = useState(editData?.ConditionValue || "");
  const [cityId, setCityId] = useState(editData?.CityId || "");
  const [areaZoneId, setAreaZoneId] = useState(editData?.AreaZoneId || "");
  const [specificMemberIds, setSpecificMemberIds] = useState(editData?.SpecificMemberIds || "");
  const [customMobileNos, setCustomMobileNos] = useState(editData?.CustomMobileNos || "");

  const [title, setTitle] = useState(editData?.Title || "");
  const [body, setBody] = useState(editData?.Body || "");
  const [category, setCategory] = useState(editData?.Category || "PROMOTIONAL");
  const [imageUrl, setImageUrl] = useState(editData?.ImageUrl || "");
  const [actionRoute, setActionRoute] = useState(editData?.ActionRoute || "HOME");
  const [actionLabel, setActionLabel] = useState(editData?.ActionLabel || "View Details");
  const [actionUrl, setActionUrl] = useState(editData?.ActionUrl || "");
  const [priority, setPriority] = useState(editData?.Priority || "NORMAL");
  
  const [sendMode, setSendMode] = useState("NOW"); // "NOW" or "SCHEDULED"
  const [scheduledDate, setScheduledDate] = useState("");

  // Master Data
  const [cityList, setCityList] = useState([]);
  const [memberList, setMemberList] = useState([]);
  const [presetTemplates, setPresetTemplates] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [estimatingRecipients, setEstimatingRecipients] = useState(false);
  const [estimatedCount, setEstimatedCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Preview Simulator State
  const [previewTab, setPreviewTab] = useState("LOCKSCREEN"); // "LOCKSCREEN" or "INAPP"
  const [previewDevice, setPreviewDevice] = useState("ANDROID"); // "ANDROID" or "IOS"

  // Fetch Cities & Members & Templates
  useEffect(() => {
    const fetchData = async () => {
      setLoadingInitial(true);
      const API_CITY_URL = `${API_WEB_URLS.MASTER}/0/token/CityMaster/Id/0`;
      const API_MEMBER_URL = `${API_WEB_URLS.MASTER}/0/token/MemberMaster/Id/0`;
      const API_TEMPLATES_URL = `${API_WEB_URLS.CUSTOM_NOTIFICATION}/0/token/Templates`;

      Fn_FillListData(dispatch, (res) => {
        if (Array.isArray(res)) setCityList(res);
        else if (res?.dataList) setCityList(res.dataList);
        else if (res?.cityList) setCityList(res.cityList);
      }, "gridData", API_CITY_URL);

      Fn_FillListData(dispatch, (res) => {
        if (Array.isArray(res)) setMemberList(res);
        else if (res?.dataList) setMemberList(res.dataList);
        else if (res?.memberList) setMemberList(res.memberList);
      }, "gridData", API_MEMBER_URL);

      Fn_FillListData(dispatch, (res) => {
        if (Array.isArray(res)) setPresetTemplates(res);
        else if (res?.dataList) setPresetTemplates(res.dataList);
      }, "gridData", API_TEMPLATES_URL);

      setLoadingInitial(false);
    };
    fetchData();
  }, [dispatch]);

  // Recipient Count Estimator
  useEffect(() => {
    // Calculate estimated recipients based on loaded member list or conditions
    if (!memberList || memberList.length === 0) {
      setEstimatedCount(audienceType === "Both" ? 250 : 120);
      return;
    }

    let count = 0;
    if (conditionType === "CUSTOM_MOBILES") {
      const numbers = customMobileNos
        .split(/[\n, ]+/)
        .filter((n) => n.trim().length >= 10);
      count = numbers.length;
    } else if (conditionType === "SPECIFIC_USERS") {
      const ids = specificMemberIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      count = ids.length;
    } else {
      let filtered = memberList.filter((m) => !m.isDeleted);
      if (audienceType === "Driver") {
        filtered = filtered.filter(
          (m) =>
            m.F_UserTypeMaster === 2 ||
            m.UserType === "Captain" ||
            m.UserType === "Driver" ||
            m.DrivingLicence
        );
      } else if (audienceType === "Customer") {
        filtered = filtered.filter(
          (m) =>
            m.F_UserTypeMaster === 1 ||
            m.UserType === "Customer" ||
            (!m.DrivingLicence && m.F_UserTypeMaster !== 2)
        );
      }

      if (cityId) {
        filtered = filtered.filter(
          (m) => String(m.F_CityMaster || m.CityId) === String(cityId)
        );
      }

      if (conditionType === "DRIVER_KYC" && audienceType === "Driver") {
        if (conditionValue === "Pending") {
          filtered = filtered.filter((m) => !m.IsApproved && !m.IsVerified);
        } else if (conditionValue === "Approved") {
          filtered = filtered.filter((m) => m.IsApproved || m.IsVerified);
        }
      }

      count = filtered.length || (audienceType === "Both" ? 180 : 90);
    }
    setEstimatedCount(count);
  }, [
    audienceType,
    conditionType,
    conditionDays,
    conditionValue,
    cityId,
    areaZoneId,
    specificMemberIds,
    customMobileNos,
    memberList,
  ]);

  // Dynamic Variable Insertion Tag click handler
  const handleInsertTag = (tag) => {
    setBody((prev) => prev + " " + tag);
  };

  // Quick Apply Preset Template
  const handleApplyTemplate = (tpl) => {
    if (!tpl) return;
    setTitle(tpl.Title || "");
    setBody(tpl.Body || "");
    if (tpl.AudienceType) setAudienceType(tpl.AudienceType);
    if (tpl.Category) setCategory(tpl.Category);
    if (tpl.ActionRoute) setActionRoute(tpl.ActionRoute);
    if (tpl.ActionLabel) setActionLabel(tpl.ActionLabel);
    if (tpl.ImageUrl) setImageUrl(tpl.ImageUrl);
    if (tpl.ConditionType) setConditionType(tpl.ConditionType);
    if (tpl.ConditionDays) setConditionDays(tpl.ConditionDays);
    if (tpl.ConditionValue) setConditionValue(tpl.ConditionValue);
    toastr.success("Template applied to composer!", "Preset Loaded");
  };

  // Preview Text with dynamic sample data resolved
  const resolvedTitle = useMemo(() => {
    const list = Array.isArray(cityList) ? cityList : [];
    return (title || "Notification Title")
      .replace(/{Name}/g, audienceType === "Driver" ? "Captain Vikram" : "Rahul Sharma")
      .replace(/{City}/g, cityId ? (list.find(c => String(c.Id) === String(cityId))?.Name || "Jaipur") : "Jaipur")
      .replace(/{WalletBalance}/g, "₹450")
      .replace(/{MobileNo}/g, "9876543210");
  }, [title, audienceType, cityId, cityList]);

  const resolvedBody = useMemo(() => {
    const list = Array.isArray(cityList) ? cityList : [];
    return (body || "Your customized push notification message text will appear here...")
      .replace(/{Name}/g, audienceType === "Driver" ? "Captain Vikram" : "Rahul Sharma")
      .replace(/{City}/g, cityId ? (list.find(c => String(c.Id) === String(cityId))?.Name || "Jaipur") : "Jaipur")
      .replace(/{WalletBalance}/g, "₹450")
      .replace(/{MobileNo}/g, "9876543210");
  }, [body, audienceType, cityId, cityList]);

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toastr.error("Please enter a notification title.", "Validation Error");
      return;
    }
    if (!body.trim()) {
      toastr.error("Please enter notification message content.", "Validation Error");
      return;
    }
    if (sendMode === "SCHEDULED" && !scheduledDate) {
      toastr.error("Please select a date and time for scheduled send.", "Validation Error");
      return;
    }

    const payload = {
      Id: Date.now(),
      Title: title || "",
      Body: body || "",
      AudienceType: audienceType || "Both",
      ConditionType: conditionType || "ALL",
      ConditionDays: Number(conditionDays) || null,
      ConditionValue: conditionValue != null ? String(conditionValue) : "",
      CityId: cityId ? Number(cityId) : null,
      CityName: cityId ? (cityList.find(c => String(c.Id) === String(cityId))?.Name || "") : "",
      AreaZoneId: areaZoneId ? Number(areaZoneId) : null,
      SpecificMemberIds: specificMemberIds || "",
      CustomMobileNos: customMobileNos || "",
      Category: category || "PROMOTIONAL",
      ImageUrl: imageUrl || "",
      ActionRoute: actionRoute || "",
      ActionLabel: actionLabel || "",
      ActionUrl: actionUrl || "",
      Priority: priority || "NORMAL",
      ScheduledDate: sendMode === "SCHEDULED" ? scheduledDate : null,
      SendStatus: sendMode === "SCHEDULED" ? "SCHEDULED" : "SENT",
      TotalRecipients: estimatedCount,
      CreatedDate: new Date().toISOString(),
    };

    setSubmitting(true);
    try {
      const url = `${API_WEB_URLS.BASE || "https://apikirayago.shinewellsofttech.co.in/api/V1/"}CustomNotification/0/token/Send`;
      
      let apiSuccess = false;
      let apiMsg = "";

      try {
        const res = await axios.post(url, payload, { timeout: 15000 });
        if (res?.data?.Success || res?.data?.success || res?.status === 200) {
          apiSuccess = true;
          apiMsg = res?.data?.Message || res?.data?.message || "";
        } else {
          apiMsg = res?.data?.Message || res?.data?.message || "Failed to send notification.";
        }
      } catch (netErr) {
        console.warn("Backend API dispatch response error:", netErr);
        const data = netErr?.response?.data;
        if (data?.errors && typeof data.errors === "object") {
          const fieldErrors = Object.entries(data.errors)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`)
            .join(" | ");
          apiMsg = fieldErrors || data?.title || netErr?.message;
        } else {
          apiMsg = data?.Message || data?.message || data?.title || netErr?.message;
        }
      }

      if (apiSuccess) {
        toastr.success(
          apiMsg || (
            sendMode === "SCHEDULED"
              ? "Notification campaign scheduled successfully!"
              : `Custom Notification dispatched successfully to ${estimatedCount.toLocaleString()} matching recipients!`
          ),
          "Broadcast Dispatched"
        );
        navigate("/NotificationHistory");
      } else {
        toastr.error(apiMsg || "Failed to dispatch notification to recipients.", "Dispatch Error");
      }
    } catch (err) {
      toastr.error(err?.message || "Failed to dispatch notification.", "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Page Header */}
        <Row className="align-items-center mb-3">
          <Col md={6}>
            <h4 className="font-size-18 mb-1 font-weight-bold text-dark">
              <i className="bx bx-broadcast text-primary me-2"></i>
              Custom Notification & Marketing Hub
            </h4>
            <p className="text-muted mb-0 font-size-13">
              Design, segment, and broadcast rich push & in-app notifications for KirayaGo Customers & Captains.
            </p>
          </Col>
          <Col md={6} className="text-md-end mt-2 mt-md-0">
            <Link to="/NotificationHistory" className="btn btn-outline-primary btn-sm rounded-pill px-3 shadow-sm me-2">
              <i className="bx bx-history me-1"></i> Campaign History
            </Link>
            <Button
              color="primary"
              className="btn-sm rounded-pill px-4 shadow-sm"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-1" /> Sending...
                </>
              ) : sendMode === "SCHEDULED" ? (
                <>
                  <i className="bx bx-calendar-event me-1"></i> Schedule Broadcast
                </>
              ) : (
                <>
                  <i className="bx bx-paper-plane me-1"></i> Send Broadcast Now
                </>
              )}
            </Button>
          </Col>
        </Row>

        {/* Quick Presets Bar */}
        <Card className="shadow-sm border-0 mb-3 bg-light">
          <CardBody className="py-2 px-3">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <span className="font-size-12 font-weight-bold text-uppercase text-secondary me-2">
                <i className="bx bx-bolt-circle text-warning me-1"></i> Quick Presets:
              </span>
              <button
                type="button"
                className="btn btn-sm btn-white border rounded-pill shadow-2xs font-size-12 px-2"
                onClick={() =>
                  handleApplyTemplate({
                    Title: "🎉 Flat 20% OFF on Your Next Ride!",
                    Body: "Hey {Name}! Enjoy 20% discount on your next ride in {City}. Use code 'KIRAYA20' at checkout.",
                    AudienceType: "Customer",
                    Category: "PROMOTIONAL",
                    ActionRoute: "OFFERS",
                    ActionLabel: "Book Ride",
                    ImageUrl: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=600&q=80",
                  })
                }
              >
                🏷️ 20% Off Promo
              </button>
              <button
                type="button"
                className="btn btn-sm btn-white border rounded-pill shadow-2xs font-size-12 px-2"
                onClick={() =>
                  handleApplyTemplate({
                    Title: "⚡ High Demand Peak Bonus Alert!",
                    Body: "Attention Captain {Name}! High booking requests right now in {City}. Earn ₹300 extra bonus on 5 rides today!",
                    AudienceType: "Driver",
                    Category: "ALERT",
                    ActionRoute: "HOME",
                    ActionLabel: "Go Online",
                    ImageUrl: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&w=600&q=80",
                  })
                }
              >
                ⚡ Driver Surge Bonus
              </button>
              <button
                type="button"
                className="btn btn-sm btn-white border rounded-pill shadow-2xs font-size-12 px-2"
                onClick={() =>
                  handleApplyTemplate({
                    Title: "👋 We miss you, {Name}! 50% Cashback",
                    Body: "It's been a while! Come back and enjoy 50% instant cashback on your next booking today.",
                    AudienceType: "Customer",
                    ConditionType: "INACTIVE_USERS",
                    ConditionDays: 15,
                    Category: "WALLET_OFFER",
                    ActionRoute: "WALLET_TOPUP",
                    ActionLabel: "Claim Cashback",
                    ImageUrl: "https://images.unsplash.com/photo-1517524008697-84bbe3c3fd98?auto=format&fit=crop&w=600&q=80",
                  })
                }
              >
                🎁 Inactive User Reactivation
              </button>
              <button
                type="button"
                className="btn btn-sm btn-white border rounded-pill shadow-2xs font-size-12 px-2"
                onClick={() =>
                  handleApplyTemplate({
                    Title: "⚠️ Complete Your Driver KYC Verification",
                    Body: "Dear Captain, your driving documents are pending verification. Upload your Driving Licence to activate your account.",
                    AudienceType: "Driver",
                    ConditionType: "DRIVER_KYC",
                    ConditionValue: "Pending",
                    Category: "KYC_ACCOUNT",
                    ActionRoute: "PROFILE_KYC",
                    ActionLabel: "Upload KYC",
                  })
                }
              >
                📑 KYC Document Reminder
              </button>
            </div>
          </CardBody>
        </Card>

        {/* Main Content: Composer Form (Left) & Simulator Preview (Right) */}
        <Row>
          {/* LEFT COLUMN: Campaign Composer */}
          <Col lg={7} xl={8}>
            {/* Step 1: Target Audience Selection */}
            <Card className="shadow-sm border-0 mb-3">
              <div className="card-header bg-white border-bottom py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="font-size-14 font-weight-bold text-dark">
                    <span className="badge bg-primary rounded-circle me-2 px-2 py-1">1</span>
                    Select Target Audience & App Role
                  </span>
                  <Badge color="success" className="p-2 font-size-12 rounded-pill shadow-xs">
                    <i className="bx bx-group me-1"></i> Matching Recipients:{" "}
                    <strong>{estimatedCount.toLocaleString()}</strong>
                  </Badge>
                </div>
              </div>
              <CardBody>
                <Row className="g-2">
                  <Col md={4}>
                    <div
                      className={`p-3 border rounded text-center cursor-pointer transition-all ${
                        audienceType === "Customer"
                          ? "border-primary bg-soft-primary shadow-sm"
                          : "bg-white hover-shadow"
                      }`}
                      onClick={() => setAudienceType("Customer")}
                      style={{ cursor: "pointer", borderWidth: audienceType === "Customer" ? "2px" : "1px" }}
                    >
                      <div className="avatar-sm mx-auto mb-2 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center">
                        <i className="bx bx-user font-size-20"></i>
                      </div>
                      <h6 className="font-size-14 mb-1 font-weight-bold">Customers / Riders</h6>
                      <p className="text-muted font-size-12 mb-0">Kirayago User Mobile App</p>
                    </div>
                  </Col>

                  <Col md={4}>
                    <div
                      className={`p-3 border rounded text-center cursor-pointer transition-all ${
                        audienceType === "Driver"
                          ? "border-warning bg-soft-warning shadow-sm"
                          : "bg-white hover-shadow"
                      }`}
                      onClick={() => setAudienceType("Driver")}
                      style={{ cursor: "pointer", borderWidth: audienceType === "Driver" ? "2px" : "1px" }}
                    >
                      <div className="avatar-sm mx-auto mb-2 bg-warning text-white rounded-circle d-flex align-items-center justify-content-center">
                        <i className="bx bx-car font-size-20"></i>
                      </div>
                      <h6 className="font-size-14 mb-1 font-weight-bold">Captains / Drivers</h6>
                      <p className="text-muted font-size-12 mb-0">Kirayago Captain App</p>
                    </div>
                  </Col>

                  <Col md={4}>
                    <div
                      className={`p-3 border rounded text-center cursor-pointer transition-all ${
                        audienceType === "Both"
                          ? "border-success bg-soft-success shadow-sm"
                          : "bg-white hover-shadow"
                      }`}
                      onClick={() => setAudienceType("Both")}
                      style={{ cursor: "pointer", borderWidth: audienceType === "Both" ? "2px" : "1px" }}
                    >
                      <div className="avatar-sm mx-auto mb-2 bg-success text-white rounded-circle d-flex align-items-center justify-content-center">
                        <i className="bx bx-broadcast font-size-20"></i>
                      </div>
                      <h6 className="font-size-14 mb-1 font-weight-bold">All Members (Both)</h6>
                      <p className="text-muted font-size-12 mb-0">Broadcast to Everyone</p>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Step 2: Smart Conditional Segmentation Engine */}
            <Card className="shadow-sm border-0 mb-3">
              <div className="card-header bg-white border-bottom py-3">
                <span className="font-size-14 font-weight-bold text-dark">
                  <span className="badge bg-primary rounded-circle me-2 px-2 py-1">2</span>
                  Smart Targeting Conditions & Filters
                </span>
              </div>
              <CardBody>
                <Row className="g-3">
                  <Col md={6}>
                    <Label className="form-label font-weight-bold font-size-12">
                      <i className="bx bx-filter-alt text-primary me-1"></i> Condition Rule
                    </Label>
                    <Input
                      type="select"
                      className="form-select"
                      value={conditionType}
                      onChange={(e) => setConditionType(e.target.value)}
                    >
                      <option value="ALL">🌐 All Selected Audience (Broadcast All)</option>
                      <option value="NEW_USERS">✨ New Users (Registered recently)</option>
                      <option value="INACTIVE_USERS">⏳ Inactive / Dormant (No recent activity)</option>
                      <option value="WALLET_BALANCE">💰 Low Wallet Balance Threshold</option>
                      {audienceType === "Driver" && (
                        <option value="DRIVER_KYC">📑 Driver KYC Verification Status</option>
                      )}
                      <option value="SPECIFIC_USERS">🎯 Specific Individual Members</option>
                      <option value="CUSTOM_MOBILES">📱 Custom Phone Numbers List</option>
                    </Input>
                  </Col>

                  <Col md={6}>
                    <Label className="form-label font-weight-bold font-size-12">
                      <i className="bx bx-map-pin text-primary me-1"></i> Target City / Region
                    </Label>
                    <Input
                      type="select"
                      className="form-select"
                      value={cityId}
                      onChange={(e) => setCityId(e.target.value)}
                    >
                      <option value="">All Cities (Nationwide / Everywhere)</option>
                      {(Array.isArray(cityList) ? cityList : []).map((c) => (
                        <option key={c.Id || c.id} value={c.Id || c.id}>
                          📍 {c.Name || c.name}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  {/* Dynamic condition parameter boxes */}
                  {conditionType === "NEW_USERS" && (
                    <Col md={12}>
                      <div className="p-3 bg-soft-info rounded border border-info">
                        <Label className="form-label font-size-12 font-weight-bold mb-1">
                          Registered Within Last (Days):
                        </Label>
                        <div className="d-flex gap-2 align-items-center">
                          {[3, 7, 15, 30].map((d) => (
                            <Button
                              key={d}
                              size="sm"
                              color={Number(conditionDays) === d ? "info" : "light"}
                              className="px-3 rounded-pill"
                              onClick={() => setConditionDays(d)}
                            >
                              Last {d} Days
                            </Button>
                          ))}
                          <Input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: "100px" }}
                            value={conditionDays}
                            onChange={(e) => setConditionDays(e.target.value)}
                            min="1"
                            max="365"
                          />
                          <span className="font-size-12 text-muted">days</span>
                        </div>
                      </div>
                    </Col>
                  )}

                  {conditionType === "INACTIVE_USERS" && (
                    <Col md={12}>
                      <div className="p-3 bg-soft-warning rounded border border-warning">
                        <Label className="form-label font-size-12 font-weight-bold mb-1">
                          Inactive (No rides/activity) For:
                        </Label>
                        <div className="d-flex gap-2 align-items-center">
                          {[7, 15, 30, 60].map((d) => (
                            <Button
                              key={d}
                              size="sm"
                              color={Number(conditionDays) === d ? "warning" : "light"}
                              className="px-3 rounded-pill"
                              onClick={() => setConditionDays(d)}
                            >
                              {d} Days Inactive
                            </Button>
                          ))}
                        </div>
                      </div>
                    </Col>
                  )}

                  {conditionType === "WALLET_BALANCE" && (
                    <Col md={12}>
                      <div className="p-3 bg-soft-danger rounded border border-danger">
                        <Label className="form-label font-size-12 font-weight-bold mb-1">
                          Wallet Balance Condition:
                        </Label>
                        <div className="d-flex gap-2">
                          {["<=0", "<=100", "<=250", "<=500"].map((v) => (
                            <Button
                              key={v}
                              size="sm"
                              color={conditionValue === v ? "danger" : "light"}
                              className="px-3 rounded-pill"
                              onClick={() => setConditionValue(v)}
                            >
                              Balance {v}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </Col>
                  )}

                  {conditionType === "DRIVER_KYC" && (
                    <Col md={12}>
                      <div className="p-3 bg-soft-primary rounded border border-primary">
                        <Label className="form-label font-size-12 font-weight-bold mb-1">
                          Driver KYC Document Status:
                        </Label>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            color={conditionValue === "Pending" ? "warning" : "light"}
                            className="px-3 rounded-pill"
                            onClick={() => setConditionValue("Pending")}
                          >
                            ⏳ Pending KYC Documents
                          </Button>
                          <Button
                            size="sm"
                            color={conditionValue === "Approved" ? "success" : "light"}
                            className="px-3 rounded-pill"
                            onClick={() => setConditionValue("Approved")}
                          >
                            ✅ Verified & Approved Captains
                          </Button>
                        </div>
                      </div>
                    </Col>
                  )}

                  {conditionType === "CUSTOM_MOBILES" && (
                    <Col md={12}>
                      <Label className="form-label font-weight-bold font-size-12">
                        Paste Comma or Line-separated Mobile Numbers:
                      </Label>
                      <Input
                        type="textarea"
                        rows="3"
                        placeholder="9876543210, 9898989898, 9123456780"
                        value={customMobileNos}
                        onChange={(e) => setCustomMobileNos(e.target.value)}
                      />
                      <small className="text-muted font-size-11">
                        Enter 10-digit mobile numbers separated by commas or new lines.
                      </small>
                    </Col>
                  )}

                  {conditionType === "SPECIFIC_USERS" && (
                    <Col md={12}>
                      <Label className="form-label font-weight-bold font-size-12">
                        Enter Member IDs or select:
                      </Label>
                      <Input
                        type="text"
                        placeholder="e.g. 101, 104, 250"
                        value={specificMemberIds}
                        onChange={(e) => setSpecificMemberIds(e.target.value)}
                      />
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>

            {/* Step 3: Notification Content & Deep Linking */}
            <Card className="shadow-sm border-0 mb-3">
              <div className="card-header bg-white border-bottom py-3">
                <span className="font-size-14 font-weight-bold text-dark">
                  <span className="badge bg-primary rounded-circle me-2 px-2 py-1">3</span>
                  Notification Content & Media
                </span>
              </div>
              <CardBody>
                <Row className="g-3">
                  {/* Category & Priority */}
                  <Col md={6}>
                    <Label className="form-label font-weight-bold font-size-12">
                      <i className="bx bx-tag text-primary me-1"></i> Category / Type
                    </Label>
                    <Input
                      type="select"
                      className="form-select"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <option value="PROMOTIONAL">🎉 Promotional / Offer</option>
                      <option value="ALERT">🚨 Critical Alert / Notice</option>
                      <option value="WALLET_OFFER">💰 Wallet & Cashback</option>
                      <option value="BOOKING_UPDATE">🚗 Booking & Ride Update</option>
                      <option value="KYC_ACCOUNT">📑 KYC & Account Verification</option>
                      <option value="CUSTOM">📢 General Announcement</option>
                    </Input>
                  </Col>

                  <Col md={6}>
                    <Label className="form-label font-weight-bold font-size-12">
                      <i className="bx bx-bell text-primary me-1"></i> Delivery Priority
                    </Label>
                    <Input
                      type="select"
                      className="form-select"
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                    >
                      <option value="NORMAL">Normal (Standard Push Notification)</option>
                      <option value="HIGH">High (Wake screen & Sound)</option>
                      <option value="URGENT">Urgent (Heads-up Persistent Banner)</option>
                    </Input>
                  </Col>

                  {/* Title */}
                  <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center">
                      <Label className="form-label font-weight-bold font-size-12 mb-1">
                        Notification Title <span className="text-danger">*</span>
                      </Label>
                      <span className="text-muted font-size-11">{title.length}/100 chars</span>
                    </div>
                    <Input
                      type="text"
                      className="form-control"
                      placeholder="e.g. 🎉 Flat 20% OFF on Weekend Rides!"
                      value={title}
                      maxLength={100}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </Col>

                  {/* Dynamic Variables Bar */}
                  <Col md={12}>
                    <div className="d-flex align-items-center flex-wrap gap-1">
                      <span className="font-size-11 text-muted font-weight-bold me-1">
                        Insert Variable:
                      </span>
                      {["{Name}", "{City}", "{WalletBalance}", "{MobileNo}"].map((tag) => (
                        <Badge
                          key={tag}
                          color="light"
                          className="text-primary border cursor-pointer font-size-11 py-1 px-2"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleInsertTag(tag)}
                        >
                          + {tag}
                        </Badge>
                      ))}
                    </div>
                  </Col>

                  {/* Message Body */}
                  <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center">
                      <Label className="form-label font-weight-bold font-size-12 mb-1">
                        Notification Message Body <span className="text-danger">*</span>
                      </Label>
                      <span className="text-muted font-size-11">{body.length}/350 chars</span>
                    </div>
                    <Input
                      type="textarea"
                      rows="3"
                      className="form-control"
                      placeholder="e.g. Hey {Name}! Book any ride in {City} and enjoy 20% instant discount. Offer valid till Sunday night!"
                      value={body}
                      maxLength={350}
                      onChange={(e) => setBody(e.target.value)}
                    />
                  </Col>

                  {/* Banner Image URL */}
                  <Col md={12}>
                    <Label className="form-label font-weight-bold font-size-12 mb-1">
                      <i className="bx bx-image text-primary me-1"></i> Banner Image URL (Optional Expandable Picture)
                    </Label>
                    <Input
                      type="text"
                      className="form-control"
                      placeholder="https://example.com/banner.jpg"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                    />
                  </Col>

                  {/* Deep-link Action Target */}
                  <Col md={6}>
                    <Label className="form-label font-weight-bold font-size-12">
                      <i className="bx bx-link-external text-primary me-1"></i> In-App Target Screen (Deep Link)
                    </Label>
                    <Input
                      type="select"
                      className="form-select"
                      value={actionRoute}
                      onChange={(e) => setActionRoute(e.target.value)}
                    >
                      <option value="HOME">🏠 Home / Booking Screen</option>
                      <option value="OFFERS">🏷️ Offers & Promo Codes</option>
                      <option value="WALLET_TOPUP">💳 Wallet Topup / Balance</option>
                      <option value="RIDE_HISTORY">📜 Ride History</option>
                      <option value="PROFILE_KYC">👤 Profile & KYC Documents</option>
                      <option value="SUPPORT">💬 Help & Live Support</option>
                    </Input>
                  </Col>

                  <Col md={6}>
                    <Label className="form-label font-weight-bold font-size-12">
                      CTA Button Label
                    </Label>
                    <Input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Book Now / Claim Offer"
                      value={actionLabel}
                      onChange={(e) => setActionLabel(e.target.value)}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Step 4: Dispatch Mode (Send Now vs Schedule) */}
            <Card className="shadow-sm border-0 mb-4">
              <div className="card-header bg-white border-bottom py-3">
                <span className="font-size-14 font-weight-bold text-dark">
                  <span className="badge bg-primary rounded-circle me-2 px-2 py-1">4</span>
                  Dispatch Timing & Schedule
                </span>
              </div>
              <CardBody>
                <div className="d-flex gap-3 mb-3">
                  <div
                    className={`p-3 border rounded flex-fill cursor-pointer ${
                      sendMode === "NOW" ? "border-primary bg-soft-primary" : "bg-white"
                    }`}
                    onClick={() => setSendMode("NOW")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center">
                      <i className="bx bx-send font-size-22 text-primary me-2"></i>
                      <div>
                        <h6 className="font-size-13 mb-0 font-weight-bold">Send Immediately</h6>
                        <small className="text-muted">Broadcast to all matched users right now</small>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`p-3 border rounded flex-fill cursor-pointer ${
                      sendMode === "SCHEDULED" ? "border-primary bg-soft-primary" : "bg-white"
                    }`}
                    onClick={() => setSendMode("SCHEDULED")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center">
                      <i className="bx bx-time-five font-size-22 text-primary me-2"></i>
                      <div>
                        <h6 className="font-size-13 mb-0 font-weight-bold">Schedule for Later</h6>
                        <small className="text-muted">Pick a specific date & time</small>
                      </div>
                    </div>
                  </div>
                </div>

                {sendMode === "SCHEDULED" && (
                  <div className="p-3 bg-light rounded border">
                    <Label className="form-label font-weight-bold font-size-12">
                      Select Date & Time (IST):
                    </Label>
                    <Input
                      type="datetime-local"
                      className="form-control"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* RIGHT COLUMN: Live Mobile Phone Simulator Preview */}
          <Col lg={5} xl={4}>
            <div style={{ position: "sticky", top: "80px" }}>
              <Card className="shadow-sm border-0">
                <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                  <span className="font-size-13 font-weight-bold text-dark">
                    <i className="bx bx-mobile text-primary me-1"></i> Live Mobile Preview
                  </span>
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      color={previewTab === "LOCKSCREEN" ? "primary" : "light"}
                      className="px-2 py-1 font-size-11 rounded"
                      onClick={() => setPreviewTab("LOCKSCREEN")}
                    >
                      Lock Screen
                    </Button>
                    <Button
                      size="sm"
                      color={previewTab === "INAPP" ? "primary" : "light"}
                      className="px-2 py-1 font-size-11 rounded"
                      onClick={() => setPreviewTab("INAPP")}
                    >
                      In-App Modal
                    </Button>
                  </div>
                </div>

                <CardBody className="p-3 bg-dark d-flex justify-content-center">
                  {/* Realistic Mobile Device Frame */}
                  <div
                    style={{
                      width: "300px",
                      minHeight: "560px",
                      backgroundColor: previewTab === "LOCKSCREEN" ? "#1a2234" : "#f8f9fa",
                      borderRadius: "36px",
                      border: "10px solid #2d3748",
                      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
                      overflow: "hidden",
                      position: "relative",
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    }}
                  >
                    {/* Speaker Notch */}
                    <div
                      style={{
                        position: "absolute",
                        top: "8px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "80px",
                        height: "14px",
                        backgroundColor: "#2d3748",
                        borderRadius: "10px",
                        zIndex: 10,
                      }}
                    />

                    {/* Status Bar */}
                    <div
                      className="d-flex justify-content-between px-4 pt-3 pb-2 font-size-11"
                      style={{ color: previewTab === "LOCKSCREEN" ? "#fff" : "#333" }}
                    >
                      <span className="font-weight-bold">09:41</span>
                      <div className="d-flex gap-1 align-items-center">
                        <i className="bx bx-wifi font-size-12"></i>
                        <i className="bx bxs-battery-full font-size-14"></i>
                      </div>
                    </div>

                    {/* LOCK SCREEN VIEW */}
                    {previewTab === "LOCKSCREEN" && (
                      <div className="px-3 pt-4">
                        {/* Lock screen clock */}
                        <div className="text-center text-white mb-4">
                          <div style={{ fontSize: "42px", fontWeight: "300", lineHeight: "1" }}>
                            09:41
                          </div>
                          <div className="font-size-12 text-white-50 mt-1">
                            Monday, September 21
                          </div>
                        </div>

                        {/* Push Notification Card */}
                        <div
                          style={{
                            backgroundColor: "rgba(255, 255, 255, 0.92)",
                            borderRadius: "16px",
                            padding: "12px",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                            backdropFilter: "blur(10px)",
                          }}
                        >
                          {/* App Info Header */}
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div className="d-flex align-items-center gap-1">
                              <div
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  backgroundColor: audienceType === "Driver" ? "#f1b44c" : "#556ee6",
                                  borderRadius: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#fff",
                                  fontSize: "10px",
                                }}
                              >
                                <i className={audienceType === "Driver" ? "bx bx-car" : "bx bx-navigation"}></i>
                              </div>
                              <span className="font-size-11 font-weight-bold text-dark">
                                {audienceType === "Driver" ? "KIRAYAGO CAPTAIN" : "KIRAYAGO"}
                              </span>
                            </div>
                            <span className="font-size-10 text-muted">now</span>
                          </div>

                          {/* Title */}
                          <div className="font-size-12 font-weight-bold text-dark mb-1">
                            {resolvedTitle}
                          </div>

                          {/* Body */}
                          <div className="font-size-11 text-secondary mb-2" style={{ lineHeight: "1.35" }}>
                            {resolvedBody}
                          </div>

                          {/* Expandable Image */}
                          {imageUrl && (
                            <div className="mb-2" style={{ borderRadius: "10px", overflow: "hidden" }}>
                              <img
                                src={imageUrl}
                                alt="Notification banner"
                                style={{ width: "100%", height: "110px", objectFit: "cover" }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            </div>
                          )}

                          {/* Action Button */}
                          {actionLabel && (
                            <div className="text-end">
                              <span
                                className="badge font-size-10 px-2 py-1"
                                style={{
                                  backgroundColor: audienceType === "Driver" ? "#f1b44c" : "#556ee6",
                                  color: "#fff",
                                }}
                              >
                                {actionLabel} &rarr;
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* IN-APP MODAL VIEW */}
                    {previewTab === "INAPP" && (
                      <div className="px-3 pt-3 h-100 d-flex flex-column justify-content-center">
                        <div
                          style={{
                            backgroundColor: "#fff",
                            borderRadius: "18px",
                            padding: "16px",
                            boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                            textAlign: "center",
                          }}
                        >
                          {imageUrl && (
                            <div className="mb-3" style={{ borderRadius: "12px", overflow: "hidden" }}>
                              <img
                                src={imageUrl}
                                alt="Banner"
                                style={{ width: "100%", height: "120px", objectFit: "cover" }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            </div>
                          )}
                          <div className="avatar-sm mx-auto mb-2 bg-soft-primary text-primary rounded-circle d-flex align-items-center justify-content-center">
                            <i className="bx bx-bell font-size-20"></i>
                          </div>
                          <h6 className="font-size-14 font-weight-bold text-dark mb-1">
                            {resolvedTitle}
                          </h6>
                          <p className="font-size-11 text-muted mb-3" style={{ lineHeight: "1.4" }}>
                            {resolvedBody}
                          </p>
                          <Button
                            color="primary"
                            size="sm"
                            className="w-100 rounded-pill py-2 font-size-12 shadow-sm font-weight-bold"
                          >
                            {actionLabel || "Continue"}
                          </Button>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-muted font-size-11 mt-1 p-0 text-decoration-none"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>

                <div className="card-footer bg-white border-top text-center py-2">
                  <small className="text-muted font-size-11">
                    <i className="bx bx-check-circle text-success me-1"></i> Preview reflects actual app font & layout.
                  </small>
                </div>
              </Card>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AddEdit_CustomNotification;
